import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Plus,
  Eye,
  Pencil,
  Trash2,
  Phone,
  Truck,
  Store,
  Loader2,
  MoreVertical,
  ArrowRight,
  History,
  FileText,
  Tag,
  User,
  Clock,
  Package,
} from "lucide-react";
import { cn } from "../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Pipeline configuration with icons
const PIPELINES = [
  { key: "active", name: "Active Orders", icon: Package, color: "text-blue-500" },
  { key: "offers", name: "Offers", icon: Tag, color: "text-purple-500" },
  { key: "to_deliver", name: "To Deliver", icon: Truck, color: "text-green-500" },
  { key: "to_pickup", name: "To Pickup", icon: Store, color: "text-orange-500" },
  { key: "history", name: "History", icon: History, color: "text-muted-foreground" },
];

// WhatsApp icon component
const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Context-aware pipeline move buttons - hide current pipeline
const PipelineMoveButtons = ({ currentPipeline, orderId, onMove }) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <ArrowRight className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Move to</div>
        <DropdownMenuSeparator />
        {PIPELINES.filter(p => p.key !== currentPipeline && p.key !== "history").map(pipeline => {
          const Icon = pipeline.icon;
          return (
            <DropdownMenuItem
              key={pipeline.key}
              onClick={() => onMove(orderId, pipeline.key)}
              className="gap-2"
            >
              <Icon className={cn("w-4 h-4", pipeline.color)} />
              {pipeline.name}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => onMove(orderId, "history")}
          className="gap-2 text-muted-foreground"
        >
          <History className="w-4 h-4" />
          Archive to History
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const OrderModal = ({ open, onClose, order, statuses, onSave, isNew = false }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    description: "",
    status: "New",
    pipeline: "active"
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: "Order details and notes..." }),
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none min-h-[150px] p-3",
      },
    },
  });

  useEffect(() => {
    if (order) {
      setFormData({
        name: order.name || "",
        phone: order.phone || "",
        email: order.email || "",
        address: order.address || "",
        description: order.description || "",
        status: order.status || "New",
        pipeline: order.pipeline || "active"
      });
      editor?.commands.setContent(order.description || "");
    } else {
      setFormData({
        name: "",
        phone: "",
        email: "",
        address: "",
        description: "",
        status: "New",
        pipeline: "active"
      });
      editor?.commands.setContent("");
    }
  }, [order, editor]);

  const handleSubmit = () => {
    if (!formData.name || !formData.phone) {
      toast.error("Name and phone are required");
      return;
    }
    
    const description = editor?.getHTML() || "";
    onSave({ ...formData, description });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{order ? "Edit Order" : "Add New Order"}</DialogTitle>
          <DialogDescription>
            {order ? "Update order details" : "Fill in the customer and order information"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Customer name"
                data-testid="order-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+40 7XX XXX XXX"
                data-testid="order-phone-input"
              />
            </div>
          </div>

          {/* Email field - new */}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="customer@email.com"
              data-testid="order-email-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <div className="border rounded-md bg-background">
              <EditorContent editor={editor} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Delivery address"
                data-testid="order-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger data-testid="order-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map(s => (
                    <SelectItem key={s.id} value={s.name}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                        {s.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Pipeline selection - only for new orders */}
          {isNew && (
            <div className="space-y-2">
              <Label htmlFor="pipeline">Add to Pipeline</Label>
              <Select
                value={formData.pipeline}
                onValueChange={(value) => setFormData(prev => ({ ...prev, pipeline: value }))}
              >
                <SelectTrigger data-testid="order-pipeline-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PIPELINES.filter(p => p.key !== "history").map(pipeline => {
                    const Icon = pipeline.icon;
                    return (
                      <SelectItem key={pipeline.key} value={pipeline.key}>
                        <div className="flex items-center gap-2">
                          <Icon className={cn("w-4 h-4", pipeline.color)} />
                          {pipeline.name}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} data-testid="save-order-button">
            {order ? "Update" : "Create"} Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ViewOrderModal = ({ order, open, onClose }) => {
  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Order Details</DialogTitle>
          <DialogDescription>View order information</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground text-xs">Name</Label>
              <p className="font-medium">{order.name}</p>
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Phone</Label>
              <div className="flex items-center gap-2">
                <p className="font-medium">{order.phone}</p>
                <a
                  href={`https://wa.me/${order.phone.replace(/[^0-9]/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-500 hover:text-green-600"
                >
                  <WhatsAppIcon className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          {/* Email field */}
          {order.email && (
            <div>
              <Label className="text-muted-foreground text-xs">Email</Label>
              <p>
                <a href={`mailto:${order.email}`} className="text-primary hover:underline">
                  {order.email}
                </a>
              </p>
            </div>
          )}

          {order.address && (
            <div>
              <Label className="text-muted-foreground text-xs">Address</Label>
              <p>{order.address}</p>
            </div>
          )}

          <div>
            <Label className="text-muted-foreground text-xs">Description</Label>
            <div
              className="prose prose-sm max-w-none mt-1 p-3 bg-muted rounded-md"
              dangerouslySetInnerHTML={{ __html: order.description || "<p>No description</p>" }}
            />
          </div>

          {/* Audit Trail */}
          <div className="border-t pt-4 space-y-2">
            <Label className="text-muted-foreground text-xs">Audit Trail</Label>
            <div className="text-sm space-y-1">
              {order.created_by && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <User className="w-3 h-3" />
                  Created by {order.created_by} on {new Date(order.created_at).toLocaleString()}
                </div>
              )}
              {order.last_edited_by && order.last_edited_by !== order.created_by && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Pencil className="w-3 h-3" />
                  Last edited by {order.last_edited_by} on {new Date(order.updated_at).toLocaleString()}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground text-xs">Status</Label>
              <p>{order.status}</p>
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Pipeline</Label>
              <p className="capitalize">{order.pipeline?.replace("_", " ")}</p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [activePipeline, setActivePipeline] = useState("active");
  const [isLoading, setIsLoading] = useState(true);
  const [editOrder, setEditOrder] = useState(null);
  const [viewOrder, setViewOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, statusesRes] = await Promise.all([
        axios.get(`${API_URL}/orders`),
        axios.get(`${API_URL}/orders/statuses`),
      ]);
      setOrders(ordersRes.data);
      setStatuses(statusesRes.data);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      toast.error("Failed to load orders");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveOrder = async (orderData) => {
    try {
      if (editOrder) {
        await axios.put(`${API_URL}/orders/${editOrder.id}`, orderData);
        toast.success("Order updated");
      } else {
        // Use the pipeline from form data for new orders
        await axios.post(`${API_URL}/orders`, orderData);
        toast.success("Order created");
      }
      setShowModal(false);
      setEditOrder(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to save order");
    }
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      await axios.delete(`${API_URL}/orders/${orderId}`);
      setDeleteConfirm(null);
      fetchData();
      toast.success("Order deleted");
    } catch (error) {
      toast.error("Failed to delete order");
    }
  };

  const handleMovePipeline = async (orderId, pipeline) => {
    try {
      await axios.put(`${API_URL}/orders/${orderId}`, { pipeline });
      fetchData();
      const pipelineLabel = PIPELINES.find(p => p.key === pipeline)?.name || pipeline;
      toast.success(`Moved to ${pipelineLabel}`);
    } catch (error) {
      toast.error("Failed to move order");
    }
  };

  const filteredOrders = orders.filter(o => o.pipeline === activePipeline);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Check mobile state on resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getStatusColor = (statusName) => {
    const status = statuses.find(s => s.name === statusName);
    return status?.color || "#6366f1";
  };

  const pipelineCounts = PIPELINES.reduce((acc, p) => {
    acc[p.key] = orders.filter(o => o.pipeline === p.key).length;
    return acc;
  }, {});

  return (
    <div className="h-full flex flex-col" data-testid="orders-page">
      {/* Header */}
      <div className="border-b border-border px-4 sm:px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-3">
          <Package className="w-5 h-5 text-primary" />
          <h1 className="text-lg sm:text-xl font-semibold">Orders</h1>
          <Badge variant="secondary" className="hidden sm:inline-flex">Global</Badge>
        </div>
        <Button onClick={() => { setEditOrder(null); setShowModal(true); }} data-testid="add-order-button" size={isMobile ? "sm" : "default"}>
          <Plus className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Add Order</span>
        </Button>
      </div>

      {/* Pipeline Tabs */}
      <Tabs value={activePipeline} onValueChange={setActivePipeline} className="flex-1 flex flex-col overflow-hidden">
        <div className="border-b border-border px-2 md:px-6 overflow-x-auto">
          <TabsList className="bg-transparent h-auto p-0 gap-1 md:gap-2 inline-flex min-w-max">
            {PIPELINES.map(pipeline => {
              const Icon = pipeline.icon;
              return (
                <TabsTrigger
                  key={pipeline.key}
                  value={pipeline.key}
                  className={cn(
                    "data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-2 md:px-3 pb-2 md:pb-3 gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap",
                    pipeline.color
                  )}
                  data-testid={`pipeline-tab-${pipeline.key}`}
                >
                  <Icon className="w-3 h-3 md:w-4 md:h-4" />
                  <span className="hidden sm:inline">{pipeline.name}</span>
                  <Badge variant="secondary" className="ml-0.5 md:ml-1 text-xs">
                    {pipelineCounts[pipeline.key]}
                  </Badge>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2 md:p-6 md:max-w-5xl md:mx-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No orders in this pipeline</p>
                {activePipeline === "active" && (
                  <Button
                    variant="link"
                    onClick={() => { setEditOrder(null); setShowModal(true); }}
                    className="mt-2"
                  >
                    Create your first order
                  </Button>
                )}
              </div>
            ) : (
              /* Mobile: Card layout, Desktop: Table */
              <>
                {/* Mobile Card View */}
                <div className="md:hidden space-y-3">
                  {filteredOrders.map((order, index) => (
                    <div 
                      key={order.id} 
                      className="border rounded-lg p-3 bg-background"
                      data-testid={`order-card-${order.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className="text-xs text-muted-foreground">#{index + 1}</span>
                          <h3 className="font-medium">{order.name}</h3>
                        </div>
                        <span
                          className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: getStatusColor(order.status) }}
                        >
                          {order.status}
                        </span>
                      </div>
                      
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <Phone className="w-3 h-3 text-muted-foreground" />
                          <span>{order.phone}</span>
                          <a
                            href={`https://wa.me/${order.phone.replace(/[^0-9]/g, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-500"
                          >
                            <WhatsAppIcon className="w-4 h-4" />
                          </a>
                        </div>
                        {order.address && (
                          <p className="text-muted-foreground truncate">{order.address}</p>
                        )}
                        <p className="text-xs text-muted-foreground">By: {order.created_by || "Unknown"}</p>
                      </div>
                      
                      <div className="flex items-center justify-end gap-1 mt-3 pt-2 border-t">
                        <Button variant="ghost" size="sm" onClick={() => setViewOrder(order)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setEditOrder(order); setShowModal(true); }}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <PipelineMoveButtons
                          currentPipeline={order.pipeline}
                          orderId={order.id}
                          onMove={handleMovePipeline}
                        />
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setDeleteConfirm(order.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop Table View */}
                <Table className="hidden md:table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created By</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrders.map((order, index) => (
                      <TableRow key={order.id} data-testid={`order-row-${order.id}`}>
                        <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                        <TableCell className="font-medium">{order.name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {order.phone}
                            <a
                              href={`https://wa.me/${order.phone.replace(/[^0-9]/g, "")}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-500 hover:text-green-600"
                              title="WhatsApp"
                            >
                              <WhatsAppIcon className="w-4 h-4" />
                            </a>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground max-w-[200px] truncate">
                          {order.address || "-"}
                        </TableCell>
                        <TableCell>
                          <span
                            className="px-2 py-1 rounded-full text-xs font-medium text-white"
                            style={{ backgroundColor: getStatusColor(order.status) }}
                          >
                            {order.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                        {order.created_by || "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <TooltipProvider delayDuration={0}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => setViewOrder(order)}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>View</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => { setEditOrder(order); setShowModal(true); }}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          
                          {/* Context-aware move buttons - full fluidity */}
                          <PipelineMoveButtons
                            currentPipeline={order.pipeline}
                            orderId={order.id}
                            onMove={handleMovePipeline}
                          />
                          
                          <TooltipProvider delayDuration={0}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive"
                                  onClick={() => setDeleteConfirm(order.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Delete</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  </TableBody>
                </Table>
              </>
            )}
          </div>
        </ScrollArea>
      </Tabs>

      {/* Modals */}
      <OrderModal
        open={showModal}
        onClose={() => { setShowModal(false); setEditOrder(null); }}
        order={editOrder}
        statuses={statuses}
        onSave={handleSaveOrder}
        isNew={!editOrder}
      />

      <ViewOrderModal
        order={viewOrder}
        open={!!viewOrder}
        onClose={() => setViewOrder(null)}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Order</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this order? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDeleteOrder(deleteConfirm)}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
